import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchIcon, PlusIcon, XCircleIcon, Columns3Icon } from 'lucide-react';
import {
  LegacyDateRangePicker,
  type LegacyDateRange,
} from '@/entities/legacy-date-range';
import { LegacyCheckAccess } from '@/entities/legacy-access';

/**
 * The toolbar above the orders table: search on the left; the column
 * selector, date range and a "New Order" call to action on the right,
 * separated from the table by a hairline rule.
 *
 * Geometry is measured off the live panel — the action controls are 48px tall
 * with a 96px (fully rounded) radius, and the button is the brand orange at
 * weight 600. The search field is intentionally more compact than the actions
 * so it reads as a light filter rather than the row's dominant control.
 *
 * The search input is CONTROLLED by the URL value and pushes on EVERY
 * keystroke — the old `InputSearch` wrote the query string per keystroke
 * (its 200ms `timeout` prop existed but the delivery page's instance updated
 * the URL in an effect on each change), so a debounce here made the port lag
 * behind the original. Spaces are stripped before storing
 * (`InputSearch.tsx:141-143` — the field searches order ids, where a space
 * can only be a paste artifact), Escape blurs the field, and a clear (×)
 * affordance appears while it holds text.
 */
interface LegacyOrdersToolbarProps {
  search: string;
  /** The active date window — rendered by the picker's trigger. */
  range: LegacyDateRange;
  onSearchChange: (value: string) => void;
  onRangeChange: (range: LegacyDateRange) => void;
  onNewOrder: () => void;
  /** The column-selector entries, in grid order (id + header label key). */
  columns: readonly { id: string; labelKey: string }[];
  /** TanStack-style map; a missing key means "visible". */
  columnVisibility: Record<string, boolean>;
  onToggleColumn: (id: string) => void;
  onResetColumns: () => void;
}

export function LegacyOrdersToolbar({
  search,
  range,
  onSearchChange,
  onRangeChange,
  onNewOrder,
  columns,
  columnVisibility,
  onToggleColumn,
  onResetColumns,
}: LegacyOrdersToolbarProps) {
  const { t } = useTranslation('legacy');

  return (
    // `flex-wrap` keeps this responsive while the compact search leaves room
    // for the column selector, date range and «Новый заказ» actions.
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--lg-grey-300)] px-4 py-3 sm:gap-4 sm:px-6">
      <label className="relative flex h-10 w-full max-w-56 min-w-0 basis-56 items-center gap-2 rounded-[96px] bg-[var(--lg-surface)] px-3 sm:w-56 sm:grow-0">
        <SearchIcon className="size-4 shrink-0 text-[var(--lg-grey-500)]" />
        <input
          type="search"
          name="search"
          value={search}
          onChange={(e) =>
            // Strip ALL spaces, exactly as the old field did — the stripped
            // value is both what the URL stores and what the input shows.
            onSearchChange(e.target.value.replaceAll(' ', ''))
          }
          onKeyDown={(e) => {
            // The old field blurred on Escape (`InputSearch.tsx:231-234`).
            if (e.key === 'Escape') e.currentTarget.blur();
          }}
          placeholder={t('search')}
          className={[
            'h-full w-full bg-transparent text-[14px] font-medium',
            'text-[var(--lg-fg)] placeholder:text-[var(--lg-grey-500)]',
            'outline-none',
            // The native ✕ of `type="search"` would double the explicit
            // clear affordance below.
            '[&::-webkit-search-cancel-button]:hidden',
          ].join(' ')}
        />
        {search !== '' && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            aria-label={t('profile.cancel')}
            className="shrink-0 cursor-pointer text-[var(--lg-grey-400)] transition-colors hover:text-[var(--lg-red-500)]"
          >
            <XCircleIcon className="size-4" />
          </button>
        )}
      </label>

      {/* `ml-auto` keeps the actions pinned right on a wrapped second line,
          where `justify-between` alone would spread them across it. */}
      {/* `min-w-0` (not `shrink-0`) so the group can compress on a phone — the
          date pill inside it drops its prefix label and truncates rather than
          pushing «Новый заказ» off the row. `ml-auto` keeps the actions pinned
          right on a wrapped second line, where `justify-between` alone would
          spread them across it. */}
      <div className="flex min-w-0 items-center gap-2 max-sm:ml-auto sm:gap-3">
        <LegacyColumnSelector
          columns={columns}
          columnVisibility={columnVisibility}
          onToggleColumn={onToggleColumn}
          onResetColumns={onResetColumns}
        />

        <LegacyDateRangePicker value={range} onChange={onRangeChange} />

        {/* The old toolbar's `delivery.create` gate (`pages/delivery/index.tsx:302`). */}
        <LegacyCheckAccess permission="delivery.create">
          <button
            type="button"
            onClick={onNewOrder}
            className={[
              'flex h-12 shrink-0 cursor-pointer items-center gap-2 rounded-[96px] px-4',
              'max-[420px]:aspect-square max-[420px]:justify-center max-[420px]:px-0',
              'bg-[var(--lg-orange-500)] text-[16px] font-semibold text-[var(--lg-surface)]',
              'transition-opacity hover:opacity-90',
            ].join(' ')}
          >
            <PlusIcon className="size-5" />
            {/* The label is the first thing to go on a phone — the orange pill
                plus a «+» still reads as "add", and keeping the words forced the
                date-range pill off the row. */}
            <span className="whitespace-nowrap max-[420px]:sr-only">
              {t('new_order')}
            </span>
          </button>
        </LegacyCheckAccess>
      </div>
    </div>
  );
}

/**
 * The column show/hide control, replacing the old AG Grid column menu (whose
 * choices lived in the redux `deliveryTableColumns` slice): a 48px toolbar
 * button opening a checkbox list of the grid's columns, plus a reset.
 *
 * The default has every column visible, matching the slice's `hide: false`
 * defaults; persistence is the page's job (localStorage), this is purely the
 * affordance.
 */
function LegacyColumnSelector({
  columns,
  columnVisibility,
  onToggleColumn,
  onResetColumns,
}: {
  columns: readonly { id: string; labelKey: string }[];
  columnVisibility: Record<string, boolean>;
  onToggleColumn: (id: string) => void;
  onResetColumns: () => void;
}) {
  const { t } = useTranslation('legacy');
  const [open, setOpen] = useState(false);

  // `table.columns` / `table.columns_reset` are not in the legacy locale yet
  // (the old app used AG Grid's own built-in menu strings); the defaults keep
  // the control usable until the keys land.
  const label = t('table.columns', { defaultValue: 'Колонки' });
  const resetLabel = t('table.columns_reset', { defaultValue: 'Сбросить' });

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={label}
        title={label}
        aria-expanded={open}
        className={[
          'flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-[96px]',
          'bg-[var(--lg-surface)] text-[var(--lg-grey-600)]',
          'transition-colors hover:text-[var(--lg-fg)]',
        ].join(' ')}
      >
        <Columns3Icon className="size-5" />
      </button>

      {open && (
        <>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- invisible click-away layer; the list below stays keyboard-operable */}
          <div className="fixed inset-0 z-[999]" onClick={() => setOpen(false)} />
          <div className="absolute top-14 right-0 z-[1000] w-[240px] rounded-[16px] border border-[var(--lg-grey-200)] bg-[var(--lg-surface)] py-2 shadow-lg">
            <ul>
              {columns.map((column) => (
                <li key={column.id}>
                  <label className="flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-[14px] font-medium text-[var(--lg-fg)] hover:bg-[var(--lg-grey-100)]">
                    <input
                      type="checkbox"
                      checked={columnVisibility[column.id] !== false}
                      onChange={() => onToggleColumn(column.id)}
                      className="size-4 cursor-pointer accent-[var(--lg-orange-500)]"
                    />
                    <span>{t(column.labelKey)}</span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="mt-1 border-t border-[var(--lg-grey-100)] px-2 pt-1">
              <button
                type="button"
                onClick={onResetColumns}
                className="w-full cursor-pointer rounded-[12px] px-2 py-2 text-left text-[14px] font-semibold text-[var(--lg-orange-600)] hover:bg-[var(--lg-grey-100)]"
              >
                {resetLabel}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
